import React, { useState, useMemo, useEffect } from "react";
import {
  BedDouble, UtensilsCrossed, Sparkles, Ship, ArrowLeft, RefreshCw,
  Search, Loader2, CalendarDays, Users, Ticket, FileText, Image as ImageIcon,
  MapPin, Accessibility, Film, Clock, ChevronRight, Layers,
} from "lucide-react";
import { useCruise } from "@/context/CruiseContext";
import { useShipReference, useBookedDining, useBookedEntertainment } from "./useShipReference";
import { DecksTab } from "./DecksTab";
import {
  matchCabinCategory, matchDiningVenue, matchEntertainment, displayShowTitle,
  toPlainText, formatUpdated, formatReservationDate, formatReservationTime,
  firstImage, safeMediaList,
} from "./shipRefUtils";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

// RC8 · Dedicated Ship Guide experience. Opened from the compact "Explore
// ship" module on Voyage Home. Default view "Your Cruise" prioritises the
// traveler's booked stateroom + dining; the catalogue tabs expose the complete
// Widgety ship-wide reference with search, filters and progressive disclosure.
// Summary-source badge wording follows the PO data-quality taxonomy (2026-09-22):
// Unavailable = no detail record at Widgety yet · Degraded = record inconsistent ·
// Transient = temporary provider error, retrying.
const QUALITY_BADGE = {
  unavailable: "Summary only · full detail not yet available from Widgety",
  degraded: "Summary only · Widgety detail record inconsistent",
  transient: "Summary only · provider error, retrying",
};

const TABS = [
  { key: "your-cruise", label: "Your Cruise", Icon: Ship },
  { key: "cabins", label: "Cabins", Icon: BedDouble },
  { key: "dining", label: "Dining", Icon: UtensilsCrossed },
  { key: "venues", label: "Venues & Entertainment", Icon: Sparkles },
  { key: "decks", label: "Decks", Icon: Layers },
];

function ExpandableText({ text }) {
  const [open, setOpen] = useState(false);
  const plain = toPlainText(text);
  if (!plain) return null;
  const long = plain.length > 180;
  return (
    <div className="mt-1.5">
      <p className={`text-[12.5px] leading-relaxed text-[#64748B] ${open || !long ? "" : "line-clamp-3"}`}>
        {plain}
      </p>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-1 text-[12px] font-semibold text-[#0EA5E9] hover:underline"
        >
          {open ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
}

function CatalogueCard({ title, badges = [], description, stats, media, menus, testId }) {
  const [open, setOpen] = useState(false);
  const images = safeMediaList(media).filter((m) => m.type === "image" || m.type === "floorplan");
  const cover = firstImage(media);
  const menuLinks = safeMediaList(menus);
  return (
    <li className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid={testId}>
      {cover ? (
        <div className="w-full aspect-[16/9] bg-[#F1F5F9] overflow-hidden">
          <img
            src={cover.url}
            alt={cover.alt || title || "Ship media"}
            loading="lazy"
            data-testid={`${testId}-cover`}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
          />
        </div>
      ) : null}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="font-semibold text-[14px] text-[#0F172A]">{title}</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {badges.filter(Boolean).map((b, i) => (
              <span key={i} className="shrink-0 rounded-full bg-[#EAF6FB] px-2 py-0.5 text-[10px] font-semibold text-[#0369A1]">
                {b}
              </span>
            ))}
          </div>
        </div>
        <ExpandableText text={description} />
        {stats ? <p className="mt-1.5 text-[11px] text-[#94A3B8]">{toPlainText(stats)}</p> : null}

        {menuLinks.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-2" data-testid={`${testId}-menus`}>
            {menuLinks.map((m, i) => (
              <a key={i} href={m.url} target="_blank" rel="noopener noreferrer"
                 data-testid={`${testId}-menu-${i}`}
                 className="inline-flex items-center gap-1 rounded-full border border-[#0EA5E9] px-3 py-1 text-[11.5px] font-semibold text-[#0369A1] hover:bg-[#F0F9FF]">
                <FileText className="w-3 h-3" /> {m.type === "menu" ? "Sample menu" : "Document"}
              </a>
            ))}
          </div>
        )}

        {images.length > 1 && (
          <div className="mt-2.5">
            <button type="button" onClick={() => setOpen((v) => !v)}
                    data-testid={`${testId}-gallery-toggle`}
                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#0EA5E9] hover:underline">
              <ImageIcon className="w-3.5 h-3.5" /> {open ? "Hide photos" : `View ${images.length} photos`}
            </button>
            {open && (
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`${testId}-gallery`}>
                {images.map((m, i) => (
                  <div key={i} className="aspect-square rounded-lg bg-[#F1F5F9] overflow-hidden">
                    <img src={m.url} alt={m.alt || `${title} ${i + 1}`} loading="lazy"
                         className="w-full h-full object-cover"
                         onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function Toolbar({ query, onQuery, filterOptions, filterValue, onFilter, tabKey, count }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
      <div className="relative flex-1 min-w-0">
        <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search…"
          data-testid={`ship-guide-search-${tabKey}`}
          className="w-full rounded-full border border-[#E2E8F0] bg-white pl-9 pr-3 py-2 text-[13px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/40"
        />
      </div>
      {filterOptions && filterOptions.length > 0 && (
        <select
          value={filterValue}
          onChange={(e) => onFilter(e.target.value)}
          data-testid={`ship-guide-filter-${tabKey}`}
          className="rounded-full border border-[#E2E8F0] bg-white px-4 py-2 text-[13px] text-[#475569] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/40"
        >
          <option value="">All</option>
          {filterOptions.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      )}
      <span className="text-[12px] text-[#94A3B8] whitespace-nowrap" data-testid={`ship-guide-count-${tabKey}`}>
        {count} shown
      </span>
    </div>
  );
}

function useFiltered(items, query, filterValue, filterField, textFields) {
  return useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items || []).filter((it) => {
      if (filterValue && filterField && (it[filterField] || "") !== filterValue) return false;
      if (!q) return true;
      return textFields.some((f) => toPlainText(it[f]).toLowerCase().includes(q));
    });
  }, [items, query, filterValue, filterField, textFields]);
}

function ReferenceEmpty() {
  return (
    <div className="py-10 text-center" data-testid="ship-guide-reference-empty">
      <p className="text-[14px] font-semibold text-[#475569]">Reference data not loaded yet</p>
      <p className="mt-1 text-[12.5px] text-[#94A3B8]">
        Cabins, dining and venues for this ship will appear here once the reference data has loaded.
      </p>
    </div>
  );
}

function CabinsTab({ cabins }) {
  const [query, setQuery] = useState("");
  const [family, setFamily] = useState("");
  const families = useMemo(
    () => Array.from(new Set((cabins || []).map((c) => c.family).filter(Boolean))).sort(),
    [cabins],
  );
  const filtered = useFiltered(cabins, query, family, "family", ["name", "description"]);
  if (!cabins.length) return <ReferenceEmpty />;
  return (
    <div>
      <Toolbar query={query} onQuery={setQuery} filterOptions={families} filterValue={family}
               onFilter={setFamily} tabKey="cabins" count={filtered.length} />
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ship-guide-list-cabins">
        {filtered.map((c, i) => (
          <CatalogueCard key={c.id || i} testId={`ship-guide-item-cabins-${i}`}
            title={c.name} badges={[c.family, c.view]} description={c.description} stats={c.stats}
            media={c.media} />
        ))}
      </ul>
    </div>
  );
}

function DiningTab({ dining }) {
  const [query, setQuery] = useState("");
  const [experience, setExperience] = useState("");
  const experiences = useMemo(
    () => Array.from(new Set((dining || []).map((d) => d.experience).filter(Boolean))).sort(),
    [dining],
  );
  const filtered = useFiltered(dining, query, experience, "experience", ["name", "description"]);
  if (!dining.length) return <ReferenceEmpty />;
  return (
    <div>
      <Toolbar query={query} onQuery={setQuery} filterOptions={experiences} filterValue={experience}
               onFilter={setExperience} tabKey="dining" count={filtered.length} />
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ship-guide-list-dining">
        {filtered.map((d, i) => (
          <CatalogueCard key={d.id || i} testId={`ship-guide-item-dining-${i}`}
            title={d.name} badges={[d.experience, d.food]} description={d.description}
            media={d.media} menus={d.menus} />
        ))}
      </ul>
    </div>
  );
}

function VenuesTab({ venues }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const categories = useMemo(
    () => Array.from(new Set((venues || []).map((v) => v.category).filter(Boolean))).sort(),
    [venues],
  );
  const filtered = useFiltered(venues, query, category, "category", ["name", "description"]);
  if (!venues.length) return <ReferenceEmpty />;
  return (
    <div>
      <Toolbar query={query} onQuery={setQuery} filterOptions={categories} filterValue={category}
               onFilter={setCategory} tabKey="venues" count={filtered.length} />
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ship-guide-list-venues">
        {filtered.map((v, i) => (
          <CatalogueCard key={v.id || i} testId={`ship-guide-item-venues-${i}`}
            title={v.name} badges={[v.categories && v.categories[0]]} description={v.description}
            media={v.media} />
        ))}
      </ul>
    </div>
  );
}

// Consistent-crop, lazy thumbnail with a reduced-motion-safe hover zoom.
function Thumb({ media, alt, testId, className = "" }) {
  const cover = firstImage(media);
  if (!cover) return null;
  return (
    <div className={`bg-[#EFE7D8] overflow-hidden ${className}`}>
      <img src={cover.url} alt={cover.alt || alt || "media"} loading="lazy"
           data-testid={testId}
           className="w-full h-full object-cover transition-transform duration-500 ease-out motion-safe:group-hover:scale-[1.04]"
           onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
    </div>
  );
}

// Human classification for a venue/experience — never raw provider taxonomy.
// Widgety `experience` is "complimentary" (included) or "cover" (cover charge
// → specialty). Anything unrecognized yields no badge rather than a raw label.
function classifyDining(experience) {
  const e = (experience || "").toLowerCase();
  if (e.includes("compliment")) return "Complimentary";
  if (e === "cover" || e.includes("cover") || e.includes("special")) return "Specialty dining";
  return null;
}

function ReservedPill() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#0B2540] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#F5EAD2]">
      <span className="w-1.5 h-1.5 rounded-full bg-[#C6A15B]" /> Reserved
    </span>
  );
}

// Card date/time — the primary operational fact — plus party size. No
// confirmation number here (it lives in the detail drawer).
function CardWhen({ date, time, party }) {
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
      {(date || time) && (
        <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#0B2540]">
          <CalendarDays className="w-3.5 h-3.5 text-[#B8894B]" />
          {date ? formatReservationDate(date) : ""}{time ? ` · ${formatReservationTime(time)}` : ""}
        </span>
      )}
      {party ? <span className="inline-flex items-center gap-1 text-[11.5px] text-[#5B6B7B]"><Users className="w-3 h-3" />Party of {party}</span> : null}
    </div>
  );
}

// Premium navy/ivory/gold placeholder for a booked occasion with no usable
// licensed image (no match, or a remote image that failed to load). Carries
// the icon, title and venue — never an empty white box.
function BrandedFallback({ Icon = Film, title, venue, testId }) {
  return (
    <div data-testid={testId}
      className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-4 text-center bg-[#0B2540]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_18%,rgba(198,161,91,0.20),transparent_62%)]" />
      <span className="relative grid place-items-center w-10 h-10 rounded-full border border-[#C6A15B]/60 text-[#C6A15B]">
        <Icon className="w-4 h-4" />
      </span>
      <div className="relative text-[12.5px] font-semibold leading-snug text-[#F5EAD2] line-clamp-2">{title}</div>
      {venue ? <div className="relative text-[10.5px] text-[#9DB0C4]">{venue}</div> : null}
    </div>
  );
}

// Card media region — ALWAYS fills the same fixed-aspect box so the absolute
// "Reserved" pill can never collapse onto the text below. Renders the licensed
// image; on a missing OR failed image it swaps to the branded fallback. A
// venue-derived image is captioned so it's never mistaken for the show.
function CardMedia({ media, mediaKind, Icon, title, venue, testId }) {
  const images = safeMediaList(media).filter((m) => m.type === "image" || !m.type);
  const [idx, setIdx] = useState(0);
  const current = images[idx] || null;   // advance through valid show images before the fallback
  return (
    <div className="relative w-full aspect-[16/10] overflow-hidden bg-[#EFE7D8]">
      {current ? (
        <>
          <img key={current.url} src={current.url} alt={current.alt || title || "media"} loading="lazy" data-testid={testId}
               className="w-full h-full object-cover transition-transform duration-500 ease-out motion-safe:group-hover:scale-[1.04]"
               onError={() => setIdx((i) => i + 1)} />
          {mediaKind === "venue" && (
            <span data-testid={`${testId}-venue-tag`}
              className="absolute bottom-2 left-2 rounded-full bg-[#0B2540]/85 px-2 py-0.5 text-[10px] font-semibold text-[#F5EAD2] backdrop-blur-sm">
              Venue photo
            </span>
          )}
        </>
      ) : (
        <BrandedFallback Icon={Icon} title={title} venue={venue} testId={`${testId}-fallback`} />
      )}
    </div>
  );
}

// A booked occasion card — fully clickable <button>, no nested controls.
function OccasionCard({ testId, onOpen, media, mediaKind, fallbackIcon, alt, name, date, time, party, venue, classification, matched }) {
  return (
    <button type="button" onClick={onOpen} data-testid={testId}
      className="group text-left w-full rounded-[18px] border border-[#ECE4D6] bg-white overflow-hidden shadow-[0_1px_2px_rgba(11,37,64,0.04)] transition-[transform,box-shadow] duration-300 motion-safe:hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-12px_rgba(11,37,64,0.25)] hover:border-[#E7D9BE] focus:outline-none focus:ring-2 focus:ring-[#C6A15B]/60">
      <div className="relative">
        <CardMedia media={media} mediaKind={mediaKind} Icon={fallbackIcon || Film}
                   title={name} venue={venue} testId={`${testId}-thumb`} />
        <div className="absolute left-2.5 top-2.5"><ReservedPill /></div>
      </div>
      <div className="p-4">
        <CardWhen date={date} time={time} party={party} />
        <div className="mt-1 font-semibold text-[14.5px] leading-snug text-[#0B2540]">{name}</div>
        {venue ? <div className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-[#5B6B7B]"><MapPin className="w-3 h-3" />{venue}</div> : null}
        <div className="mt-2.5 flex items-center justify-between gap-2">
          {classification
            ? <span className="rounded-full bg-[#FBF3E2] px-2.5 py-0.5 text-[11px] font-semibold text-[#8A6A2F]">{classification}</span>
            : <span className="text-[11px] text-[#94A3B8]" data-testid={`${testId}-fallback`}>{matched ? "" : "Booking details only"}</span>}
          <span className="shrink-0 inline-flex items-center gap-0.5 text-[12px] font-semibold text-[#B8894B] group-hover:underline">
            View details <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </button>
  );
}

// Expandable gallery inside the detail drawer.
function DrawerGallery({ media, title }) {
  const [open, setOpen] = useState(false);
  const imgs = safeMediaList(media).filter((m) => m.type === "image" || m.type === "floorplan" || !m.type);
  const cover = firstImage(media);
  if (!cover) return null;
  return (
    <div>
      <div className="w-full aspect-[16/9] rounded-2xl bg-[#EFE7D8] overflow-hidden">
        <img src={cover.url} alt={cover.alt || title} loading="lazy" data-testid="ship-guide-detail-cover"
             className="w-full h-full object-cover"
             onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
      </div>
      {imgs.length > 1 && (
        <>
          <button type="button" onClick={() => setOpen((v) => !v)} data-testid="ship-guide-detail-gallery-toggle"
                  className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-[#B8894B] hover:underline">
            <ImageIcon className="w-3.5 h-3.5" /> {open ? "Hide photos" : `View ${imgs.length} photos`}
          </button>
          {open && (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="ship-guide-detail-gallery">
              {imgs.map((m, i) => (
                <div key={i} className="aspect-square rounded-xl bg-[#EFE7D8] overflow-hidden">
                  <img src={m.url} alt={m.alt || `${title} ${i + 1}`} loading="lazy" className="w-full h-full object-cover"
                       onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DrawerGroup({ label, children }) {
  return (
    <section className="pt-3 first:pt-0">
      <div className="cc-overline text-[#B8894B] mb-1.5">{label}</div>
      {children}
    </section>
  );
}

// Right-side (desktop) / full-screen (mobile) detail drawer. Radix Sheet gives
// focus trap, Escape, scroll-lock and focus restore. A history entry makes the
// browser Back button close the drawer instead of leaving the Ship Guide.
function DetailDrawer({ detail, onClose }) {
  const open = !!detail;
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ shipGuideDrawer: true }, "");
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open, onClose]);

  const d = detail || {};
  const menus = safeMediaList(d.menus);
  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" data-testid="ship-guide-detail-drawer"
        className="w-full sm:max-w-md overflow-y-auto p-0 bg-[#FDFBF6]">
        <div className="p-5">
          <SheetHeader className="text-left">
            <SheetTitle className="text-[19px] font-bold leading-tight text-[#0B2540]" data-testid="ship-guide-detail-title">{d.title}</SheetTitle>
            {d.headline ? <SheetDescription className="text-[12.5px] text-[#5B6B7B]">{d.headline}</SheetDescription> : <SheetDescription className="sr-only">Details</SheetDescription>}
          </SheetHeader>

          <div className="mt-4 divide-y divide-[#EDE3D0] space-y-4 [&>section]:pt-4">
            {(d.reservation || d.confirmation) && (
              <DrawerGroup label="Your reservation">
                {d.reservation}
                {d.confirmation ? (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-[#5B6B7B]" data-testid="ship-guide-detail-confirmation">
                    <Ticket className="w-3.5 h-3.5 text-[#B8894B]" /> Confirmation {d.confirmation}
                  </div>
                ) : null}
              </DrawerGroup>
            )}

            <DrawerGroup label={d.aboutLabel || "About"}>
              <DrawerGallery media={d.media} title={d.title} />
              {d.mediaKind === "venue" && firstImage(d.media) ? (
                <p className="mt-1.5 text-[11px] text-[#8A97A5]" data-testid="ship-guide-detail-venue-note">
                  Photo shows the venue, not the specific performance.
                </p>
              ) : null}
              {d.classification ? (
                <span className="mt-2 inline-block rounded-full bg-[#FBF3E2] px-2.5 py-0.5 text-[11px] font-semibold text-[#8A6A2F]">{d.classification}</span>
              ) : null}
              {d.description ? <p className="mt-2 text-[12.5px] leading-relaxed text-[#3D4B59]">{toPlainText(d.description)}</p> : null}
              {d.stats ? <p className="mt-1.5 text-[11.5px] text-[#8A97A5]">{toPlainText(d.stats)}</p> : null}

              {menus.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="ship-guide-detail-menus">
                  {menus.map((m, i) => (
                    <a key={i} href={m.url} target="_blank" rel="noopener noreferrer" data-testid={`ship-guide-detail-menu-${i}`}
                       className="inline-flex items-center gap-1 rounded-full border border-[#C6A15B] px-3 py-1.5 text-[11.5px] font-semibold text-[#8A6A2F] hover:bg-[#FBF3E2]">
                      <FileText className="w-3 h-3" /> {m.type === "menu" ? "Sample menu" : "Document"}
                    </a>
                  ))}
                </div>
              )}

              {Array.isArray(d.facilities) && d.facilities.length > 0 && (
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1" data-testid="ship-guide-detail-facilities">
                  {d.facilities.map((f, i) => (
                    <li key={i} className="text-[11.5px] text-[#3D4B59] flex items-start gap-1.5">
                      <span className="mt-1 w-1 h-1 rounded-full bg-[#C6A15B] shrink-0" />{toPlainText(f)}
                    </li>
                  ))}
                </ul>
              )}
              {Array.isArray(d.grade_codes) && d.grade_codes.length > 0 && (
                <div className="mt-2 text-[11px] text-[#8A97A5]" data-testid="ship-guide-detail-grades">Category codes: {d.grade_codes.join(", ")}</div>
              )}
              {d.accessibleCategory && (
                <div className="mt-2 inline-flex items-start gap-1.5 text-[11.5px] text-[#3D4B59]" data-testid="ship-guide-detail-accessibility">
                  <Accessibility className="w-3.5 h-3.5 mt-0.5 text-[#B8894B]" />
                  Accessible staterooms are available in this category. Confirm your specific stateroom’s accessibility with the cruise line.
                </div>
              )}
              {!d.matched && d.enrichable ? (
                <p className="mt-2 text-[12px] text-[#8A97A5]" data-testid="ship-guide-detail-fallback">Additional details aren’t available for this booking.</p>
              ) : null}
              {d.matched ? (
                <div className="mt-3 text-[10px] uppercase tracking-wide text-[#A9B4C0]" data-testid="ship-guide-detail-attribution">
                  {d.mediaKind === "venue" ? "Venue information enriched by Widgety" : "Details and media enriched by Widgety"}
                </div>
              ) : null}
            </DrawerGroup>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SectionHeader({ icon: Icon, title, count }) {
  return (
    <div className="flex items-baseline gap-2 mb-3">
      <Icon className="w-4 h-4 text-[#B8894B] self-center" />
      <h3 className="text-[15px] font-bold text-[#0B2540]">{title}</h3>
      {count ? <span className="text-[12px] font-medium text-[#8A97A5]">· {count}</span> : null}
    </div>
  );
}

function YourCruiseTab({ cabins, dining, venues, reservations, events, referenceReady, onExploreCabins }) {
  const { voyageMeta } = useCruise();
  const [detail, setDetail] = useState(null);

  const cabinNumber = voyageMeta?.cabin_number || voyageMeta?.stateroom || null;
  const cabinCategory = voyageMeta?.cabin_category || null;   // authoritative
  const deck = voyageMeta?.deck || null;                       // authoritative
  const mc = matchCabinCategory(voyageMeta?.cabin_category_id, cabins, cabinCategory);
  const cabinFacilities = (mc?.facilities || []).map((f) => (typeof f === "string" ? f : f?.name)).filter(Boolean);

  const diningSorted = useMemo(
    () => [...(reservations || [])].sort((a, b) => (a.date || "").localeCompare(b.date || "")),
    [reservations],
  );
  const entSorted = useMemo(
    () => [...(events || [])].sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.time || "").localeCompare(b.time || "")),
    [events],
  );

  const drawerReservation = (r) => (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-[#0B2540]">
      {(r.date || r.time) && <span className="inline-flex items-center gap-1 font-semibold"><CalendarDays className="w-3.5 h-3.5 text-[#B8894B]" />{r.date ? formatReservationDate(r.date) : ""}{r.time ? ` · ${formatReservationTime(r.time)}` : ""}</span>}
      {r.party_size ? <span className="inline-flex items-center gap-1 text-[#5B6B7B]"><Users className="w-3 h-3" />Party of {r.party_size}</span> : null}
    </div>
  );

  const openCabin = () => setDetail({
    type: "cabin", title: mc ? mc.name : (cabinCategory || `Cabin ${cabinNumber}`),
    headline: [cabinNumber && `Cabin ${cabinNumber}`, deck && `Deck ${deck}`].filter(Boolean).join(" · "),
    aboutLabel: "About your stateroom",
    media: mc?.media, description: mc?.description, stats: mc?.stats,
    facilities: cabinFacilities, grade_codes: mc?.grade_codes, accessibleCategory: !!mc?.accessible,
    matched: !!mc, enrichable: true,
  });

  return (
    <div className="space-y-8" data-testid="ship-guide-panel-your-cruise">
      {/* Cabin feature card — editorial anchor. Begins immediately below the
          header; the personalized summary lives in the header itself. */}
      <section data-testid="ship-guide-booked-cabin">
        {cabinNumber || cabinCategory ? (
          mc ? (
            <button type="button" onClick={openCabin} data-testid="ship-guide-cabin-card"
              className="group text-left w-full rounded-[20px] border border-[#E7D9BE] bg-[#FBF7EF] overflow-hidden shadow-[0_1px_2px_rgba(11,37,64,0.05)] transition-[transform,box-shadow] duration-300 motion-safe:hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-16px_rgba(11,37,64,0.28)] focus:outline-none focus:ring-2 focus:ring-[#C6A15B]/60 flex flex-col sm:flex-row">
              <Thumb media={mc.media} alt={mc.name} testId="ship-guide-cabin-thumb"
                     className="w-full sm:w-[43%] h-44 sm:h-auto shrink-0 sm:min-h-[220px]" />
              <div className="p-5 flex-1 min-w-0 flex flex-col justify-center">
                <div className="cc-overline text-[#B8894B]">Your stateroom</div>
                <div className="mt-1 text-[22px] font-bold leading-tight text-[#0B2540]" data-testid="ship-guide-cabin-number">Cabin {cabinNumber}</div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
                  <span className="font-semibold text-[#0B2540]" data-testid="ship-guide-cabin-category">{cabinCategory}</span>
                  {deck && <span className="inline-flex items-center gap-1 text-[#5B6B7B]" data-testid="ship-guide-cabin-deck"><MapPin className="w-3.5 h-3.5 text-[#B8894B]" />Deck {deck}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ship-guide-cabin-facts">
                  {[mc.family, mc.view && `${mc.view} view`].filter(Boolean).map((b, i) => (
                    <span key={i} className="rounded-full bg-white/70 border border-[#ECE0C8] px-2.5 py-0.5 text-[11px] font-medium text-[#5B4B2E]">{b}</span>
                  ))}
                </div>
                {mc.description ? <p className="mt-2.5 text-[12.5px] leading-relaxed text-[#3D4B59] line-clamp-2">{toPlainText(mc.description)}</p> : null}
                <span className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[#B8894B] group-hover:underline">
                  Explore your cabin <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </button>
          ) : (
            <div className="rounded-[20px] border border-[#E7D9BE] bg-[#FBF7EF] p-5" data-testid="ship-guide-cabin-unmatched">
              <div className="cc-overline text-[#B8894B]">Your stateroom</div>
              <div className="mt-1 text-[22px] font-bold text-[#0B2540]">Cabin {cabinNumber}</div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
                {cabinCategory && <span className="font-semibold text-[#0B2540]">{cabinCategory}</span>}
                {deck && <span className="inline-flex items-center gap-1 text-[#5B6B7B]"><MapPin className="w-3.5 h-3.5 text-[#B8894B]" />Deck {deck}</span>}
              </div>
              <p className="mt-2 text-[12px] text-[#8A97A5]">We don’t have matching stateroom photos or details for this category yet.</p>
              {referenceReady && (
                <button type="button" onClick={onExploreCabins} data-testid="ship-guide-explore-cabins"
                        className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#C6A15B] px-4 py-2 text-[12px] font-semibold text-[#8A6A2F] hover:bg-[#FBF3E2]">
                  Browse all cabin categories
                </button>
              )}
            </div>
          )
        ) : (
          <div className="rounded-[20px] border border-[#E7D9BE] bg-[#FBF7EF] p-5 text-[13px] text-[#8A97A5]">Your cabin will be assigned at check-in.</div>
        )}
      </section>

      {/* Booked dining */}
      <section data-testid="ship-guide-booked-dining">
        <SectionHeader icon={UtensilsCrossed} title="Your dining reservations"
          count={diningSorted.length ? `${diningSorted.length} reserved` : null} />
        {diningSorted.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ship-guide-dining-grid">
            {diningSorted.map((r, i) => {
              const mv = matchDiningVenue(r.venue_name, dining);
              const classification = classifyDining(mv?.experience);
              return (
                <OccasionCard key={r.id || i} testId={`ship-guide-dining-card-${i}`}
                  onOpen={() => setDetail({
                    type: "dining", title: r.venue_name, aboutLabel: "About the venue",
                    reservation: drawerReservation(r), confirmation: r.confirmation_number,
                    media: mv?.media, description: mv?.description, menus: mv?.menus,
                    classification, matched: !!mv, enrichable: true,
                  })}
                  media={mv?.media} alt={r.venue_name} name={r.venue_name}
                  fallbackIcon={UtensilsCrossed}
                  date={r.date} time={r.time} party={r.party_size}
                  classification={classification} matched={!!mv} />
              );
            })}
          </div>
        ) : (
          <div className="rounded-[18px] border border-[#ECE4D6] bg-white p-4 text-[13px] text-[#8A97A5]">You have no dining reservations yet.</div>
        )}
      </section>

      {/* Booked entertainment */}
      <section data-testid="ship-guide-booked-entertainment">
        <SectionHeader icon={Film} title="Your entertainment"
          count={entSorted.length ? `${entSorted.length} show${entSorted.length === 1 ? "" : "s"}` : null} />
        {entSorted.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ship-guide-entertainment-grid">
            {entSorted.map((ev, i) => {
              const m = matchEntertainment(ev.title, ev.venue, venues);
              const rec = m?.record;
              const showTitle = displayShowTitle(ev.title);   // guest-facing (typo-corrected); matching uses ev.title
              const conf = (ev.subtitle && /(\d{5,})/.test(ev.subtitle)) ? ev.subtitle.match(/(\d{5,})/)[1] : null;
              return (
                <OccasionCard key={ev.id || i} testId={`ship-guide-entertainment-card-${i}`}
                  onOpen={() => setDetail({
                    type: "entertainment", title: showTitle, aboutLabel: "About the show",
                    reservation: drawerReservation({ date: ev.date, time: ev.time }), confirmation: conf,
                    media: rec?.media, description: rec?.description,
                    classification: rec ? "Show" : null, matched: !!rec, mediaKind: m?.mediaKind, enrichable: true,
                  })}
                  media={rec?.media} mediaKind={m?.mediaKind} fallbackIcon={Film}
                  alt={showTitle} name={showTitle}
                  date={ev.date} time={ev.time} venue={ev.venue}
                  classification={rec ? "Show" : null} matched={!!rec} />
              );
            })}
          </div>
        ) : (
          <div className="rounded-[18px] border border-[#ECE4D6] bg-white p-4 text-[13px] text-[#8A97A5]" data-testid="ship-guide-entertainment-empty">
            No entertainment reservations for this voyage yet.
          </div>
        )}
      </section>

      <DetailDrawer detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
export default function ShipGuide({ initialTab }) {
  const { voyageMeta, setRoute } = useCruise();
  const shipId = voyageMeta?.ship_id;
  const shipName = voyageMeta?.ship_name || "";
  const ref = useShipReference(shipId, shipName);
  const { reservations } = useBookedDining();
  const { events } = useBookedEntertainment();
  const [active, setActive] = useState(
    TABS.some((t) => t.key === initialTab) ? initialTab : "your-cruise",
  );
  const [deckDetail, setDeckDetail] = useState(null);

  // Deep-link follow-up: `#ship-guide:<tab>` may change while this page is
  // already mounted (same component position), so honour the new tab.
  useEffect(() => {
    if (TABS.some((t) => t.key === initialTab)) setActive(initialTab);
  }, [initialTab]);

  // Hash routing keeps the previous page's scroll offset; a tab/page change
  // must start at the top (otherwise Voyage Home's scroll lands mid-"Your
  // Cruise", e.g. on entertainment reservations).
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); } catch { /* jsdom */ }
  }, [active]);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const goBack = () => setRoute(isMobile ? "today" : "dashboard");

  const referenceReady = !ref.loading && !ref.denied && !ref.error &&
    (ref.cabins.length + ref.dining.length + ref.venues.length) > 0;
  const isWidgety = ref.meta.source === "widgety";
  const updated = formatUpdated(ref.meta.updated_at);

  // Personalized summary — no ship name (it's the title) and no Widgety
  // attribution (page-level, right of the title). Shown for Your Cruise only.
  const cabinNumber = voyageMeta?.cabin_number || voyageMeta?.stateroom || null;
  const summaryBits = [
    cabinNumber && `Cabin ${cabinNumber}`,
    `${reservations.length} dining reservation${reservations.length === 1 ? "" : "s"}`,
    `${events.length} show${events.length === 1 ? "" : "s"}`,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="ship-guide">
      {/* Header */}
      <div className="px-5 sm:px-8 pt-5 pb-5 text-white"
           style={{ background: "linear-gradient(135deg, #0F2244 0%, #14315e 100%)" }}>
        <button
          type="button"
          onClick={goBack}
          data-testid="ship-guide-back"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white/80 hover:text-white mt-2 mb-5"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/70">Ship Guide</div>
            <h1 className="mt-1 font-display font-black tracking-tight text-white text-2xl sm:text-3xl"
                data-testid="ship-guide-title">
              {shipName || ref.meta.ship_name || "Your ship"}
            </h1>
          </div>
          {isWidgety && (
            <div className="text-[11px] text-white/70" data-testid="ship-guide-attribution">
              <span className="font-semibold text-white">Enrichment by Widgety</span>
              {updated ? <> · Updated {updated}</> : null}
              {ref.meta.degraded ? (
                <span className="ml-2 inline-flex items-center rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold text-white"
                      data-testid="ship-guide-quality-badge">
                  {QUALITY_BADGE[ref.meta.quality?.category] || QUALITY_BADGE.unavailable}
                </span>
              ) : null}
            </div>
          )}
        </div>

        {/* Compact personalized line — sits between the title and navigation. */}
        {active === "your-cruise" && summaryBits.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-1" data-testid="ship-guide-your-cruise-summary">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#C6A15B]">
              <Ship className="w-3.5 h-3.5" /> Your voyage edition
            </span>
            {summaryBits.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-2.5 text-[12.5px] font-medium text-[#E7EDF4]">
                <span className="text-[#C6A15B]" aria-hidden="true">·</span>{b}
              </span>
            ))}
          </div>
        )}

        {/* Navigation — the header's bottom row. Wraps on narrow screens so all
            controls stay reachable without nested scrolling or clipping. */}
        <div className="mt-5 flex flex-wrap gap-2" role="tablist"
             data-testid="ship-guide-tabs">
          {TABS.map((t) => {
            const on = active === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setActive(t.key)}
                data-testid={`ship-guide-tab-${t.key}`}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-[12.5px] font-semibold transition-colors ${
                  on ? "bg-white text-[#0F2244]" : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                <t.Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6">
        {ref.loading ? (
          <div className="flex items-center gap-2 text-[13px] text-[#64748B] py-12 justify-center"
               data-testid="ship-guide-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading ship reference…
          </div>
        ) : ref.error ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center" data-testid="ship-guide-error">
            <p className="text-[13px] text-[#64748B]">We couldn’t load ship reference data just now.</p>
            <button
              type="button"
              onClick={ref.reload}
              data-testid="ship-guide-retry"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0EA5E9] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:brightness-95"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Try again
            </button>
          </div>
        ) : (
          <>
            {active === "your-cruise" && (
              <YourCruiseTab cabins={ref.cabins} dining={ref.dining} venues={ref.venues}
                             reservations={reservations} events={events}
                             referenceReady={referenceReady}
                             onExploreCabins={() => setActive("cabins")} />
            )}
            {active === "cabins" && (
              ref.denied ? <ReferenceEmpty /> : <CabinsTab cabins={ref.cabins} />
            )}
            {active === "dining" && (
              ref.denied ? <ReferenceEmpty /> : <DiningTab dining={ref.dining} />
            )}
            {active === "venues" && (
              ref.denied ? <ReferenceEmpty /> : <VenuesTab venues={ref.venues} />
            )}
            {active === "decks" && (
              ref.denied ? <ReferenceEmpty /> : (
                <>
                  <DecksTab decks={ref.decks} cabinDeck={voyageMeta?.deck} cabinNumber={cabinNumber}
                            dining={ref.dining} venues={ref.venues} onOpenVenue={setDeckDetail} />
                  <DetailDrawer detail={deckDetail} onClose={() => setDeckDetail(null)} />
                </>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}
